// src/pos-app/surfaces/waiter/TopBarExtras.js
//
// F12a-N Task W5 — the waiter surface's top-bar extras (design contract:
// docs/mockups/f12a-n/waiter-tables.html `#btnSales`, `.tb-btn`). Mounts
// into StationTopBar's `children` slot (native mode only — see
// WaiterStation.js). Deliberately minimal: WaiterStation currently mounts
// NO extras there at all (unlike CashierStation's TopBarExtras, which
// carries a session pill/Orders badge/More menu), so this is just the one
// "Sales" button — and, once in sales-mode, the "Back to floor" chip that
// replaces it.
//
// No context bridge (contrast CashierStation's SellExtrasProvider pattern,
// TopBarExtras.js in ../cashier/): nothing here needs data plucked out of a
// deeply-nested sibling subtree. `salesMode`/`onOpenSalesPin`/`onBackToFloor`
// are plain props straight from WaiterStation, which is the direct parent of
// both this component (top bar) and TablesSurface/SellSurface (body) — a
// bridge would be pure ceremony here.
//
// The PIN modal itself is NOT rendered by this component — WaiterStation
// mounts <SalesPinModal/> directly (sibling of the station frame, same
// level as StationSettingsSheet) so its Escape handling can be centralized
// into TablesSurface's single keydown authority (see that file's header)
// via the `salesPinOpen`/`onCloseSalesPin` props WaiterStation threads
// straight through, the same way `locked` already is.
import React from 'react';
import { useLanguage } from '../../../context/LanguageContext';

const CSS = `
.wte-sales{display:inline-flex;align-items:center;justify-content:center;gap:7px;
  min-height:44px;padding:0 16px;border-radius:12px;cursor:pointer;
  background:var(--c-surface);border:1px solid var(--c-border);color:var(--c-ink);
  font-family:inherit;font-size:13px;font-weight:800;white-space:nowrap;}
.wte-sales:hover{border-color:var(--c-greenBorder);color:var(--c-green);}
.wte-sales svg{flex:none;}
.wte-back{display:inline-flex;align-items:center;justify-content:center;gap:7px;
  min-height:44px;padding:0 16px;border-radius:12px;cursor:pointer;
  background:var(--c-greenLight);border:1px solid var(--c-greenBorder);color:var(--c-green);
  font-family:inherit;font-size:13px;font-weight:800;white-space:nowrap;}
.wte-back svg{flex:none;}
`;

const CashierIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4H6Z" />
        <path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
);

const BackIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
);

export default function TopBarExtras({ salesMode, onOpenSalesPin, onBackToFloor }) {
    const { t } = useLanguage();
    return (
        <>
            <style>{CSS}</style>
            {salesMode ? (
                <button type="button" className="wte-back" onClick={onBackToFloor}>
                    {BackIcon}
                    {t('back_to_floor')}
                </button>
            ) : (
                <button
                    type="button"
                    className="wte-sales"
                    onClick={onOpenSalesPin}
                    aria-label={t('switch_to_cashier_sales')}
                >
                    {CashierIcon}
                    {t('sales')}
                </button>
            )}
        </>
    );
}
